import type { DocNode as ADF } from '@atlaskit/adf-schema';
import type { MediaFile } from '@atlaskit/media-picker';
import type { UserOption } from '@atlassian/jira-base-user-picker/src/types.tsx';
import {
	mapUserOptionsToIds,
	mapUserOptionsToAccountIds,
} from '@atlassian/jira-issue-create-commons/src/common/ui/fields/base-fields/user-picker-field/utils.tsx';
import { getFileIdsFromFiles } from '@atlassian/jira-issue-create-commons/src/ui/attachments-field/utils.tsx';
import type { UserValue } from '@atlassian/jira-issue-field-assignee/src/common/types.tsx';
import type { SelectedOption } from '@atlassian/jira-issue-field-cascading-select/src/common/types.tsx';
import { transformToServerValue } from '@atlassian/jira-issue-field-cascading-select/src/common/utils.tsx';
import type { Options as CheckboxOptions } from '@atlassian/jira-issue-field-checkbox-select/src/ui/edit/types.tsx';
import { mapOptionsToData as mapCheckboxSelectOptionsToData } from '@atlassian/jira-issue-field-checkbox-select/src/ui/edit/utils.tsx';
import type { TeamFieldType } from '@atlassian/jira-issue-field-custom-team/src/common/types.tsx';
import type { UserPickerOption } from '@atlassian/jira-issue-field-group-picker/src/common/types.tsx';
import { mapLabelOptionsToData } from '@atlassian/jira-issue-field-labels/src/common/utils.tsx';
import type { Option as PriorityOption } from '@atlassian/jira-issue-field-priority/src/ui/edit/types.tsx';
import { mapOptionToData as mapPriorityOptionToData } from '@atlassian/jira-issue-field-priority/src/ui/edit/utils.tsx';
import type { Option as RadioOption } from '@atlassian/jira-issue-field-radio-select/src/ui/edit/types.tsx';
import { mapOptionToData as mapRadioSelectOptionToData } from '@atlassian/jira-issue-field-radio-select/src/ui/edit/utils.tsx';
import type { SelectOption } from '@atlassian/jira-issue-field-select/src/ui/edit/types.tsx';
import { mapSelectOptionsToData } from '@atlassian/jira-issue-field-select/src/ui/edit/utils.tsx';
import { convertTextToAdf } from '@atlassian/jira-issue-field-textarea/src/ui/edit/utils.tsx';
import type { LabelOption as LabelsOption } from '@atlassian/jira-issue-shared-types/src/common/types/labels-type.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { FieldWriter } from '../types.tsx';
import type {
	JiraMultipleSelectFieldInputWritable,
	JiraSelectedOptionInput,
	JiraStringFieldInputWritable,
	JiraLabelsFieldInputWritable,
	JiraPriorityInput,
	JiraUserFieldInput,
	JiraADFFieldInput,
	JiraGroupInput,
	JiraUserInput,
} from './types.tsx';

const toMultipleSelectField = (id: string, ids: string[]): JiraMultipleSelectFieldInputWritable => {
	const toOptionInput = (optionId: string): JiraSelectedOptionInput => ({ id: optionId });
	return {
		fieldId: id,
		options: ids.map(toOptionInput),
	};
};

const toStringField = (id: string, value: string): JiraStringFieldInputWritable => ({
	fieldId: id,
	value,
});

export const toLabelsField = (id: string, labels: string[]): JiraLabelsFieldInputWritable => ({
	fieldId: id,
	values: {
		values: labels,
	},
});

const toPriorityField = (priorityId: string): JiraPriorityInput => ({
	priorityId,
});

export const toUserField = (id: string, accountId: string): JiraUserFieldInput => ({
	fieldId: id,
	user: {
		accountId,
	},
});

const toADFField = (id: string, adf: ADF): JiraADFFieldInput => {
	const { version } = adf;
	return {
		fieldId: id,
		adf: {
			version,
			jsonValue: adf,
		},
	};
};

const toGroupInput = (pickerOption: UserPickerOption): JiraGroupInput => ({
	groupName: pickerOption.name,
});

export const textFieldWriter: FieldWriter<string> = (id, value, fields) => {
	fields.stringFields.push(toStringField(id, value));
};

export const selectFieldWriter: FieldWriter<SelectOption | null> = (id, value, fields) => {
	if (value) {
		fields.stringFields.push(toStringField(id, String(mapSelectOptionsToData(value).id)));
	}
};

export const checkboxSelectFieldWriter: FieldWriter<CheckboxOptions> = (id, value, fields) => {
	fields.multipleSelectFields.push(
		toMultipleSelectField(
			id,
			mapCheckboxSelectOptionsToData(value).map((o) => o.id),
		),
	);
};

export const textAreaFieldWriter: FieldWriter<string | ADF> = (id, value, fields) => {
	if (value instanceof Object && fg('forms_multiline_text_wiki_renderer')) {
		return;
	}

	// @ts-expect-error Remove this typecheck suppression on forms_multiline_text_wiki_renderer cleanup ts(2345)
	fields.adfFields.push(toADFField(id, convertTextToAdf(value)));
};

export const dateFieldWriter: FieldWriter<string> = (id, value, fields) => {
	fields.stringFields.push(toStringField(id, value));
};

export const labelsFieldWriter: FieldWriter<LabelsOption[]> = (id, value, fields) => {
	fields.labels.push(toLabelsField(id, mapLabelOptionsToData(value)));
};

export const priorityFieldWriter: FieldWriter<PriorityOption> = (_id, value, fields) => {
	// eslint-disable-next-line no-param-reassign
	fields.priority = toPriorityField(mapPriorityOptionToData(value).id);
};

export const numberFieldWriter: FieldWriter<string> = (id, value, fields) => {
	fields.stringFields.push(toStringField(id, value));
};

export const dateTimeFieldWriter: FieldWriter<string> = (id, value, fields) => {
	fields.stringFields.push(toStringField(id, value));
};

export const urlFieldWriter: FieldWriter<string> = (id, value, fields) => {
	fields.stringFields.push(toStringField(id, value));
};

export const multiUserPickerFieldWriter: FieldWriter<UserOption[]> = (id, value, fields) => {
	const accountIds = mapUserOptionsToAccountIds(value);
	fields.multipleSelectUserPickerFields.push({
		fieldId: id,
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		users: accountIds as JiraUserInput[],
	});
};

export const cascadingSelectFieldWriter: FieldWriter<SelectedOption> = (id, value, fields) => {
	const option = transformToServerValue(value);
	if (option !== null) {
		fields.stringFields.push(toStringField(id, option.id));
		if (option.child) {
			fields.stringFields.push(toStringField(`${id}:1`, option.child.id));
		}
	}
};

export const singleUserPickerFieldWriter: FieldWriter<UserOption | null | undefined> = (
	id,
	value,
	fields,
) => {
	if (!value) {
		return;
	}

	const result = mapUserOptionsToIds(value);
	if (!Array.isArray(result)) {
		fields.users.push(toUserField(id, result.id));
	}
};

export const radioSelectFieldWriter: FieldWriter<RadioOption> = (id, value, fields) => {
	fields.stringFields.push(toStringField(id, mapRadioSelectOptionToData(value).id));
};

export const multiSelectFieldWriter: FieldWriter<{ value?: string | number | null }[]> = (
	id,
	value,
	fields,
) => {
	fields.multipleSelectFields.push(
		toMultipleSelectField(
			id,
			value
				.map((o) => o.value)
				.filter(
					(selectedValue): selectedValue is string | number =>
						typeof selectedValue === 'string' || typeof selectedValue === 'number',
				)
				.map((o) => String(o)),
		),
	);
};

export const attachmentFieldWriter: FieldWriter<MediaFile[]> = (_id, value, fields) => {
	if (Array.isArray(value) && value.length >= 1) {
		const ids = getFileIdsFromFiles(value);
		fields.stringFields.push(toStringField('attachment', ids.join(',')));
	}
};

export const descriptionFieldWriter: FieldWriter<ADF | string | undefined> = (
	id,
	value,
	fields,
) => {
	if (!value) {
		return;
	}

	if (typeof value === 'string' && fg('forms_multiline_text_wiki_renderer')) {
		// On forms_multiline_text_wiki_renderer cleanup, merge this early exit with one above:
		// I.e. if (!value || typeof value === 'string')
		return;
	}

	// @ts-expect-error Remove this typecheck suppression on forms_multiline_text_wiki_renderer cleanup ts(2345)
	fields.adfFields.push(toADFField(id, value));
};

export const singleGroupPickerFieldWriter: FieldWriter<UserPickerOption> = (id, value, fields) => {
	const group = toGroupInput(value);
	fields.singleGroupPickerFields.push({ fieldId: id, group });
};

export const multipleGroupPickerFieldWriter: FieldWriter<UserPickerOption[]> = (
	id,
	value,
	fields,
) => {
	const groups = value.map(toGroupInput);
	fields.multipleGroupPickerFields.push({ fieldId: id, groups });
};

export const teamFieldWriter: FieldWriter<TeamFieldType> = (id, value, fields) => {
	if (value) {
		fields.stringFields.push(toStringField(id, value.value));
	}
};

export const assigneeFieldWriter: FieldWriter<UserValue> = (id, value, fields) => {
	if (value?.accountId) {
		fields.users.push(toUserField(id, value.accountId));
	}
};
